import type { IGraphClient } from '../../../src/services/GraphService';
import { deadlinesFor, fieldsOf, getPack, type IPack, type IPackDeadline, type IPackField, retentionOf } from '../../api/src/packs';
import type { IWorkspace } from '../../api/src/workspace';

/**
 * How long a filed record is kept, written on the document itself in two library columns:
 * "Record category" and "Keep until" (the document's date plus the category's years; empty when the
 * firm's own policy applies). It works on every Microsoft 365 plan and is what the spot-audit
 * export and reminders read. It doesn't stop deletion: where the firm's plan includes retention
 * labels and an admin has created them (retention/labels.ts), each record also gets its category's
 * label, which does (docs/retention-spike.md).
 *
 * Supports a firm's record-keeping; the firm remains responsible for it.
 */

export const RECORD_CATEGORY_COLUMN = { name: 'AIAdminRecordCategory', displayName: 'Record category' };
export const KEEP_UNTIL_COLUMN = { name: 'AIAdminKeepUntil', displayName: 'Keep until' };

/** "2025-03-31" + 10 years → "2035-03-31" (29 Feb becomes 28 Feb in a non-leap year). */
export function keepUntil(documentDate: string | undefined, years: number | undefined): string | undefined {
  const m: RegExpMatchArray | null = (documentDate ?? '').match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m || !years) {
    return undefined;
  }
  const year: number = Number(m[1]) + years;
  const month: number = Number(m[2]);
  const lastDay: number = new Date(Date.UTC(year, month, 0)).getUTCDate();
  return `${year}-${m[2]}-${String(Math.min(Number(m[3]), lastDay)).padStart(2, '0')}`;
}

/** One filed document: its id, and the type and date it was filed with. */
export interface IFiledRecord {
  itemId: string;
  docType?: string;
  date?: string;
  /** The pack's extra facts it was filed with (e.g. expiryDate), by field id. */
  extra?: { [fieldId: string]: string };
}

/** A decision's extra facts ([{ id, value }]) as a record's { id: value }. */
export function extraById(values: { id: string; value: string }[] | undefined): { [fieldId: string]: string } | undefined {
  return values && values.length ? Object.fromEntries(values.map(v => [v.id, v.value])) : undefined;
}

/** The library column for one of a pack's extra facts: "Expiry date" is AIAdminExpiryDate. */
export function factColumn(f: IPackField): { name: string; displayName: string } {
  return { name: `AIAdmin${f.id.charAt(0).toUpperCase()}${f.id.slice(1)}`, displayName: f.label };
}

/** The library column for a deadline worked out from those facts: "Leave deadline" is AIAdminLeaveDeadline. */
export function deadlineColumn(d: IPackDeadline): { name: string; displayName: string } {
  return factColumn({ id: d.id, label: d.label, kind: 'date', meaning: d.meaning });
}

/**
 * Column values for the extra facts a document was filed with (only its type's own, only real dates),
 * and the deadline they start. Records are only written after filing, and a date that starts a
 * deadline is never filed without a person, so the deadline written is one a person approved.
 */
export function factFields(pack: IPack, record: IFiledRecord): { [column: string]: string } {
  const out: { [column: string]: string } = {};
  for (const f of fieldsOf(pack, record.docType)) {
    const value: string | undefined = record.extra?.[f.id]?.trim();
    if (!value || (f.kind === 'date' && !/^\d{4}-\d{2}-\d{2}$/.test(value))) {
      continue;
    }
    out[factColumn(f).name] = f.kind === 'date' ? `${value}T00:00:00Z` : value.slice(0, 255);
  }
  for (const d of deadlinesFor(pack, record.docType, record.extra)) {
    if (d.date) {
      out[deadlineColumn(d.deadline).name] = `${d.date}T00:00:00Z`;
    }
  }
  return out;
}

/** The column values for one document, or undefined when the pack keeps no schedule for its type. */
export function retentionFields(pack: IPack, record: IFiledRecord): { [column: string]: string | null } | undefined {
  const category = retentionOf(pack, record.docType);
  if (!category) {
    return undefined;
  }
  const until: string | undefined = keepUntil(record.date, category.years);
  return {
    [RECORD_CATEGORY_COLUMN.name]: category.label,
    // Graph takes a date column's value as an ISO date-time.
    [KEEP_UNTIL_COLUMN.name]: until ? `${until}T00:00:00Z` : null
  };
}

export interface IRetentionResult {
  written: number;
  /** Documents that got their Microsoft retention label (only when labels were created). */
  labelled?: number;
  /** Plain words for History or a status line; empty when everything was written. */
  problem?: string;
}

/** Writes the record category and keep-until date on filed documents (creating the two columns once). */
export class RetentionWriter {
  private _listId: Promise<string> | undefined;
  private _columnsReady: Promise<void> | undefined;

  constructor(
    private readonly _graph: IGraphClient,
    private readonly _siteId: string,
    private readonly _driveId: string,
    private readonly _pack: IPack,
    /** Record category id → retention label name, from the workspace; none = columns only. */
    private readonly _labels: { [recordCategoryId: string]: string } = {}
  ) {}

  /** Adds the two columns now (setup, by someone allowed to change the library); filing then only fills them in. */
  public prepare(): Promise<void> {
    return this._ensureColumns();
  }

  public async apply(records: IFiledRecord[]): Promise<IRetentionResult> {
    // How long it's kept, plus any extra facts it was filed with (immigration: expiry and decision dates).
    const work = records
      .map(r => ({ r, fields: { ...retentionFields(this._pack, r), ...factFields(this._pack, r) } }))
      .filter(x => Object.keys(x.fields).length > 0);
    if (work.length === 0) {
      return { written: 0 };
    }
    const problems: string[] = [];
    let written: number = 0;
    let columns: boolean = true;
    try {
      await this._ensureColumns();
    } catch {
      columns = false;
      problems.push('The "Record category" and "Keep until" columns couldn\'t be added to the library, so how long these documents are kept wasn\'t recorded on them.');
    }
    if (columns) {
      for (const { r, fields } of work) {
        try {
          await this._graph.api(`/drives/${this._driveId}/items/${r.itemId}/listItem/fields`).patch(fields);
          written++;
        } catch {
          // Keep going: one document that can't be updated doesn't stop the others.
        }
      }
      if (written < work.length) {
        const n: number = work.length - written;
        problems.push(`How long to keep ${n} document${n === 1 ? '' : 's'} couldn't be recorded on ${n === 1 ? 'it' : 'them'}.`);
      }
    }
    // Retention labels on top of the columns, where the firm created them. A label that can't be
    // set (no licence, no permission) never undoes or blocks the columns.
    const toLabel = work.map(x => ({ r: x.r, name: this._labels[retentionOf(this._pack, x.r.docType)?.id ?? ''] })).filter(x => !!x.name);
    if (toLabel.length === 0) {
      return problems.length ? { written, problem: problems.join(' ') } : { written };
    }
    let labelled: number = 0;
    for (const { r, name } of toLabel) {
      try {
        await this._graph.api(`/drives/${this._driveId}/items/${r.itemId}/retentionLabel`).patch({ name });
        labelled++;
      } catch {
        // Keep going, as above.
      }
    }
    if (labelled < toLabel.length) {
      const n: number = toLabel.length - labelled;
      problems.push(`The retention label couldn't be put on ${n} document${n === 1 ? '' : 's'}: the firm's Microsoft 365 plan or permissions may not allow it. Filing wasn't affected.`);
    }
    return problems.length ? { written, labelled, problem: problems.join(' ') } : { written, labelled };
  }

  private _list(): Promise<string> {
    if (!this._listId) {
      this._listId = this._graph.api(`/drives/${this._driveId}/list?$select=id`).get().then((l: { id: string }) => l.id);
    }
    return this._listId;
  }

  private _ensureColumns(): Promise<void> {
    if (!this._columnsReady) {
      this._columnsReady = (async () => {
        const base: string = `/sites/${this._siteId}/lists/${await this._list()}/columns`;
        const existing: { value: { name: string }[] } = await this._graph.api(`${base}?$select=name`).get();
        const has = (name: string): boolean => existing.value.some(c => c.name === name);
        if (!has(RECORD_CATEGORY_COLUMN.name)) {
          await this._graph.api(base).post({ ...RECORD_CATEGORY_COLUMN, text: {} });
        }
        if (!has(KEEP_UNTIL_COLUMN.name)) {
          await this._graph.api(base).post({ ...KEEP_UNTIL_COLUMN, dateTime: { format: 'dateOnly' } });
        }
        // A column for each extra fact the pack reads, e.g. "Expiry date".
        for (const f of this._pack.fields ?? []) {
          const column = factColumn(f);
          if (!has(column.name)) {
            await this._graph.api(base).post(f.kind === 'date' ? { ...column, dateTime: { format: 'dateOnly' } } : { ...column, text: {} });
          }
        }
        // And one for each deadline those facts start, e.g. "Leave deadline".
        for (const d of this._pack.deadlines ?? []) {
          const column = deadlineColumn(d);
          if (!has(column.name)) {
            await this._graph.api(base).post({ ...column, dateTime: { format: 'dateOnly' } });
          }
        }
      })();
      // A failed attempt can be tried again next time.
      this._columnsReady.catch(() => { this._columnsReady = undefined; });
    }
    return this._columnsReady;
  }
}

/** Whether filing writes how long records are kept: only packs with a retention schedule, and not AI-designed folders. */
export function keepsRecords(pack: IPack, mode: IWorkspace['mode']): boolean {
  return pack.recordCategories.length > 0 && mode !== 'folders';
}

/** The documents that were filed: everything asked for, except the ones with a problem. */
export function filedRecords(records: IFiledRecord[], problems: { [itemId: string]: string }): IFiledRecord[] {
  return records.filter(r => !problems[r.itemId]);
}

/**
 * Writes the record category and keep-until date on documents that were just filed, for packs that
 * keep a schedule (law). Never throws: filing has already happened, so a problem only comes back in plain words.
 */
export async function recordRetention(graph: IGraphClient, w: Pick<IWorkspace, 'siteId' | 'driveId' | 'packId' | 'mode' | 'retentionLabels'>, records: IFiledRecord[]): Promise<IRetentionResult | undefined> {
  const pack: IPack = getPack(w.packId);
  if (records.length === 0 || !keepsRecords(pack, w.mode)) {
    return undefined;
  }
  try {
    return await new RetentionWriter(graph, w.siteId, w.driveId, pack, w.retentionLabels).apply(records);
  } catch {
    return { written: 0, problem: 'The documents were filed, but how long to keep them couldn\'t be recorded on them.' };
  }
}
